// Custom country dropdown — button + listbox styled to match the Loqate
// suggestions list. Replaces the native <select>, which can't be styled
// consistently across browsers when open.
//
// Markup contract (server / Pug renders just the shell — JS builds the
// option rows from `data-countries`):
//
//   <div class="addr-country" data-countries="GB,IN,FR">
//     <button class="addr-country__btn" type="button"
//             aria-haspopup="listbox" aria-expanded="false">
//       <span class="addr-country__current"></span>
//     </button>
//     <ul class="addr-country__list" role="listbox" hidden></ul>
//     <input type="hidden" name="country">
//   </div>
//
// To add a new country: drop a `<symbol id="XX">` into the sprite at
// /img/flags.svg, then list its ISO 3166-1 alpha-2 code in any widget's
// `data-countries`. Names come from `Intl.DisplayNames` (localized to
// the browser's language; falls back to the code itself when the API
// isn't available). No metadata table to maintain.
//
// Backwards-compatibility: if a widget's <ul> is already populated when
// init runs (e.g. a Thymeleaf template chose to render options
// server-side), we leave the rows alone and only wire up behaviour.

const DEFAULT_COUNTRY = 'GB';
const SVG_NS = 'http://www.w3.org/2000/svg';

// Path to the flag sprite. The default suits the local dev build (assets
// at `dist/img/`); the Spring integration sits behind a `/skin/` prefix
// (or similar), so the host page can override by stamping a
// `data-flag-sprite="/skin/img/flags.svg"` attribute onto <html> or
// <body>. Read once at module load — the path is expected to be static
// for the lifetime of the page.
const FLAG_SPRITE_URL =
  document.documentElement.dataset.flagSprite
  || document.body?.dataset.flagSprite
  || 'img/flags.svg';

const displayNames = (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function')
  ? new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' })
  : null;

function regionName(code) {
  if (!displayNames) return code;
  try { return displayNames.of(code) || code; }
  catch { return code; }
}

function makeOption(code, isDefault) {
  const li = document.createElement('li');
  li.className = 'addr-country__option';
  li.setAttribute('role', 'option');
  li.dataset.value = code;
  if (isDefault) li.setAttribute('aria-selected', 'true');

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'addr-country__flag');
  svg.setAttribute('viewBox', '0 0 20 15');
  svg.setAttribute('aria-hidden', 'true');
  const use = document.createElementNS(SVG_NS, 'use');
  use.setAttribute('href', `${FLAG_SPRITE_URL}#${code}`);
  svg.appendChild(use);

  const name = document.createElement('span');
  name.className = 'addr-country__name';
  name.textContent = regionName(code);

  li.append(svg, name);
  return li;
}

// Scans the page for `.addr-country` elements marked as standalone (i.e.
// not inside a [data-address-form] wrap, which initialises its own).
// Called from main.js.
export function initCountrySelects() {
  document.querySelectorAll('.addr-country[data-country-select]').forEach(initCountrySelect);
}

// Initialise a single country widget. Returns a handle the caller can
// use to read or update the selection programmatically (address-form.js
// uses it during prefill / reset). Returns null if the markup is
// incomplete — missing button / list / hidden input, or no usable
// country list.
export function initCountrySelect(el) {
  const btn     = el.querySelector('.addr-country__btn');
  const list    = el.querySelector('.addr-country__list');
  const input   = el.querySelector('input[name="country"]');
  const current = btn?.querySelector('.addr-country__current');
  if (!btn || !list || !input) return null;

  // If `data-countries` is set, build the option rows from it. Otherwise
  // assume they're already in the markup (server-rendered) and just wire
  // behaviour onto what's there.
  const codesAttr = el.dataset.countries;
  if (codesAttr) {
    const codes = codesAttr.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    list.replaceChildren(...codes.map((c, i) => makeOption(c, i === 0)));
  }

  const options = Array.from(list.querySelectorAll('.addr-country__option'));
  if (options.length === 0) return null;

  const allowedCountries = options.map(o => (o.dataset.value || '').toUpperCase());
  const initialOpt = options.find(o => o.getAttribute('aria-selected') === 'true') || options[0];
  const defaultCountry = (initialOpt?.dataset.value || allowedCountries[0] || DEFAULT_COUNTRY).toUpperCase();

  function findOption(code) {
    const c = (code || '').toUpperCase();
    return options.find(o => (o.dataset.value || '').toUpperCase() === c) || null;
  }

  // Copy the option's flag + name into the trigger so the visible
  // selection always mirrors whatever the option markup looks like —
  // no need for the JS to know which DOM children to expect.
  function cloneOptionContent(opt) {
    return Array.from(opt.children).map(c => c.cloneNode(true));
  }

  function setCountry(code) {
    const opt = findOption(code);
    if (!opt) return;
    input.value = opt.dataset.value.toUpperCase();
    if (current) current.replaceChildren(...cloneOptionContent(opt));
    options.forEach(o => o.setAttribute('aria-selected', String(o === opt)));
    // Fire input so existing form listeners (e.g. clear-error handlers)
    // see the change.
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function open()   { list.hidden = false; btn.setAttribute('aria-expanded', 'true'); }
  function close()  { list.hidden = true;  btn.setAttribute('aria-expanded', 'false'); }
  function toggle() { list.hidden ? open() : close(); }

  options.forEach(opt => {
    opt.addEventListener('mousedown', e => e.preventDefault());
    opt.addEventListener('click', () => {
      setCountry(opt.dataset.value);
      close();
      btn.focus();
    });
  });

  setCountry(defaultCountry);

  btn.addEventListener('click', toggle);
  btn.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });
  document.addEventListener('click', e => {
    if (list.hidden) return;
    if (!el.contains(e.target)) close();
  });

  return {
    setCountry,
    getCountry()    { return input.value; },
    isAllowed(code) { return allowedCountries.includes((code || '').toUpperCase()); },
    defaultCountry,
    allowedCountries,
  };
}
